
import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Download, X, FileImage, FileText } from "lucide-react";

interface NewStudentsPageSelectionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  totalNewStudents: number;
  onDownload: (format: 'jpg' | 'pdf', startPage: number, endPage: number) => void;
}

const NEW_STUDENTS_PER_PAGE = 15;

const NewStudentsPageSelectionDialog = ({ 
  isOpen, 
  onClose, 
  totalNewStudents, 
  onDownload 
}: NewStudentsPageSelectionDialogProps) => {
  const [downloadOption, setDownloadOption] = useState("all");
  const [startPage, setStartPage] = useState(1);
  const [endPage, setEndPage] = useState(1);
  const [selectedFormat, setSelectedFormat] = useState<'jpg' | 'pdf'>('jpg');

  const totalPages = Math.ceil(totalNewStudents / NEW_STUDENTS_PER_PAGE);

  const handleDownload = () => {
    console.log('New students page selection download clicked:', { selectedFormat, downloadOption, startPage, endPage });
    
    if (downloadOption === "all") {
      onDownload(selectedFormat, 1, totalPages);
    } else {
      const validStartPage = Math.max(1, Math.min(startPage, totalPages));
      const validEndPage = Math.max(validStartPage, Math.min(endPage, totalPages));
      onDownload(selectedFormat, validStartPage, validEndPage);
    }
    onClose();
  };

  const handleStartPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value) || 1;
    setStartPage(value);
    if (value > endPage) {
      setEndPage(value);
    }
  };

  const handleEndPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value) || 1;
    setEndPage(Math.max(value, startPage));
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  const isValidRange = startPage >= 1 && endPage <= totalPages && startPage <= endPage;

  console.log('NewStudentsPageSelectionDialog render:', { isOpen, totalNewStudents, selectedFormat });

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            Download New Students Report
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-6 w-6 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </DialogTitle>
          <DialogDescription>
            Choose format, page range for downloading the new students report
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          <div className="text-sm text-muted-foreground">
            Total New Students: {totalNewStudents} ({totalPages} pages, {NEW_STUDENTS_PER_PAGE} students per page)
          </div>

          {/* Format Selection - First and Most Prominent */}
          <div className="space-y-4">
            <Label className="text-base font-semibold">Download Format</Label>
            <RadioGroup 
              value={selectedFormat} 
              onValueChange={(value) => {
                console.log('New students format changed to:', value);
                setSelectedFormat(value as 'jpg' | 'pdf');
              }}
            >
              <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 transition-colors">
                <RadioGroupItem value="jpg" id="format-jpg" />
                <div className="flex items-center space-x-3 flex-1">
                  <FileImage className="h-5 w-5 text-blue-600" />
                  <div>
                    <Label htmlFor="format-jpg" className="text-base font-medium cursor-pointer">
                      JPG Image
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      High-quality image format, ultra-fast download
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 transition-colors">
                <RadioGroupItem value="pdf" id="format-pdf" />
                <div className="flex items-center space-x-3 flex-1">
                  <FileText className="h-5 w-5 text-red-600" />
                  <div>
                    <Label htmlFor="format-pdf" className="text-base font-medium cursor-pointer">
                      PDF Document
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Professional document format, perfect for printing
                    </p>
                  </div>
                </div>
              </div>
            </RadioGroup>
          </div>
          
          {/* Page Selection - Second */}
          <div className="space-y-4">
            <Label className="text-base font-semibold">Page Selection</Label>
            <RadioGroup value={downloadOption} onValueChange={setDownloadOption}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="all" id="all" />
                <Label htmlFor="all">Download all pages</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="range" id="range" />
                <Label htmlFor="range">Download specific page range</Label>
              </div>
            </RadioGroup>

            {downloadOption === "range" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="startPage">Start Page</Label>
                  <Input
                    id="startPage"
                    type="number"
                    min={1}
                    max={totalPages}
                    value={startPage}
                    onChange={handleStartPageChange}
                  />
                  <div className="text-xs text-muted-foreground">
                    Min: 1, Max: {totalPages}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="endPage">End Page</Label>
                  <Input
                    id="endPage"
                    type="number"
                    min={startPage}
                    max={totalPages}
                    value={endPage}
                    onChange={handleEndPageChange}
                  />
                  <div className="text-xs text-muted-foreground">
                    Min: {startPage}, Max: {totalPages}
                  </div>
                </div>
              </div>
            )}

            {downloadOption === "range" && !isValidRange && (
              <p className="text-sm text-destructive">
                Please enter a valid page range (1-{totalPages})
              </p>
            )}
          </div>

          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              onClick={handleDownload} 
              disabled={totalNewStudents === 0 || (downloadOption === "range" && !isValidRange)}
            >
              <Download className="w-4 h-4 mr-2" />
              Download {selectedFormat.toUpperCase()}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NewStudentsPageSelectionDialog;
